/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import { createSelector } from 'reselect';
import { extractImmediateDescendants } from '../utils/hierarchyHelpers';
import { filterBySearchText } from '../utils/textSearchHelper';
import { WorkPackageStatuses } from '../constants/storyStatusTypes';

const getShowTrash = (state) => state.trashReducer.showTrash;
const getAllWorkPackages = (state) => state.workPackageReducer.all;
const getStories = (state) => state.workPackageReducer.stories;
const getEpics = (state) => state.workPackageReducer.epics;
const getCapabilities = (state) => state.workPackageReducer.capabilities;
const getFeatures = (state) => state.workPackageReducer.features;
const getTargets = (state) => state.targetReducer.targets;
const getDeployments = (state) => state.deploymentReducer.deployments;
const getSearchText = (state) =>
  state.defaultOptionsReducer.workPackageSearchText.toLowerCase();
const getPlacement = (_, props) => props.placement;
const getCurrentLevel = (_, props) => props.level;
const getProductFromProps = (_, props) => props.product;
const getMemberFromProps = (_, props) => props.member;
const getTeamIdFromProps = (_, props) => props.teamId;
const getTeamFromProps = (_, props) => props.team;

const getResultFromProps = (_, props) => props.result;
const getResultsFromProps = (_, props) => props.results;

const getCurrentOrganization = (state, props) =>
  state.organizationReducer.organizations.find(
    (org) => org.id === props.selectedSpace.organization_id
  );

const getCurrentOrgLevelId = (_, props) =>
  props.result.result_type.organization_level_id;

export const targetWorkPackagesSelector = createSelector(
  getStories,
  getFeatures,
  getCapabilities,
  getEpics,
  getTargets,
  (stories, features, capabilities, epics, targets) => {
    const workPackages = [...epics, ...capabilities, ...features, ...stories];
    const targetWorkPackages = {};

    if (targets && targets.length > 0) {
      targets.forEach((target) => {
        // initialize empty array for each target
        if (!targetWorkPackages[target.id]) targetWorkPackages[target.id] = [];

        targetWorkPackages[target.id] = targetWorkPackages[target.id].concat(
          workPackages.filter(
            (workPackage) => workPackage.target_id === target.id
          )
        );
      });
    }

    return targetWorkPackages;
  }
);

export const cadenceWorkPackagesSelector = createSelector(
  getAllWorkPackages,
  (workPackages) => {
    const cadenceWorkPackages = {};

    if (workPackages && workPackages.length > 0) {
      workPackages.forEach((workPackage) => {
        workPackage.cadence_ids.forEach((cadenceId) => {
          if (!cadenceWorkPackages[cadenceId])
            cadenceWorkPackages[cadenceId] = [];

          cadenceWorkPackages[cadenceId] = cadenceWorkPackages[
            cadenceId
          ].concat(workPackage);
        });
      });
    }

    return cadenceWorkPackages;
  }
);

export const deploymentWorkPackagesSelector = createSelector(
  getAllWorkPackages,
  getDeployments,
  (workPackages, deployments) => {
    const deploymentWorkPackages = {};

    if (deployments && deployments.length > 0) {
      deployments.forEach((deployment) => {
        if (!deploymentWorkPackages[deployment.id])
          deploymentWorkPackages[deployment.id] = [];
        deploymentWorkPackages[deployment.id] = deploymentWorkPackages[
          deployment.id
        ].concat(
          workPackages.filter(
            (workPackage) =>
              workPackage.deployment &&
              workPackage.deployment.id === deployment.id
          )
        );
      });
    }

    return deploymentWorkPackages;
  }
);

export const workPackagesWithChildren = createSelector(
  getShowTrash,
  getAllWorkPackages,
  getCurrentLevel,
  getSearchText,
  (showTrash, workPackages, level, searchText) => {
    let scope = workPackages.filter(
      (workPackage) => workPackage.level <= level
    );
    if (!showTrash)
      scope = scope.filter((workPackage) => !workPackage.deleted_at);
    const topLevelPackages = scope.filter(
      (workPackage) => workPackage.level === level
    );
    const topLevelPackageIDs = topLevelPackages.map(
      (workPackage) => workPackage.id
    );

    const descendants = extractImmediateDescendants(topLevelPackageIDs, scope, level);

    return filterBySearchText(
      [...topLevelPackages, ...descendants],
      searchText
    );
  }
);

export const gettingReadyWorkPackages = createSelector(
  getAllWorkPackages,
  (workPackages) =>
    workPackages.filter((wp) => wp.state === WorkPackageStatuses.GETTING_READY)
);

const getPassedInResults = (_, __, results) => results;
const getUseCadence = (_, __, ___, useCadence) => useCadence;
const getUserIds = (_, props) => props.selectedUsers;
const getTargetIds = (_, props) => props.selectedTargets;
const getCadence = (_, props) => props.currentCadence;
const getLevelFromRouter = (_, props) => parseInt(props.match.params.level, 10);

const filteredResultsBySearchText = createSelector(
  [getPassedInResults, getSearchText],
  (results, searchText) => filterBySearchText(results, searchText)
);

export const filterResultsByLevelAndText = createSelector(
  [getAllWorkPackages, getLevelFromRouter, getSearchText],
  (results, level, searchText) => {
    const resultsForLevel = results.filter((r) => r.level === level);
    return filterBySearchText(resultsForLevel, searchText);
  }
);

export const cardWallFiltering = createSelector(
  [
    filteredResultsBySearchText,
    getUserIds,
    getTargetIds,
    getCadence,
    getShowTrash,
    getUseCadence
  ],
  (results, selectedUsers, selectedTargets, cadence, showTrash, useCadence) => {
    let filteredResults = results || [];

    if (cadence && useCadence) {
      filteredResults = filteredResults.filter((r) =>
        r.cadence_ids.includes(cadence.id)
      );
    }

    if (selectedUsers.length > 0) {
      filteredResults = filteredResults.filter((result) => {
        if (!result.owner_id && selectedUsers.includes('unassigned'))
          return true;
        return selectedUsers.includes(result.owner_id);
      });
    }

    if (selectedTargets.length > 0) {
      filteredResults = filteredResults.filter((r) => {
        if (selectedTargets.includes('unassigned')) {
          return !r.target_id || selectedTargets.includes(r.target_id);
        }
        return selectedTargets.includes(r.target_id);
      });
    }

    if (!showTrash) {
      filteredResults = filteredResults.filter((r) => !r.deleted_at);
    }

    return filteredResults;
  }
);

export const filterEpicsByPropsProduct = createSelector(
  [getEpics, getProductFromProps],
  (epics, product) => epics.filter((epic) => epic.product_id === product.id)
);

export const filterResultsByPropsProduct = createSelector(
  [getAllWorkPackages, getProductFromProps],
  (results, product) => results.filter((r) => r.product_id === product.id)
);

export const sortedProductResults = createSelector(
  [filterResultsByPropsProduct],
  (results) => results.sort((a, b) => b.level - a.level)
);

export const filterAvailableOrgLevels = createSelector(
  [getCurrentOrganization, getCurrentOrgLevelId],
  (organization, currentOrgLevelId) => {
    const { organization_levels } = organization;
    return organization_levels.filter(
      (level) => level.id !== currentOrgLevelId
    );
  }
);

export const filterResultsByTeamAndOwner = createSelector(
  [getAllWorkPackages, getMemberFromProps, getTeamIdFromProps, getShowTrash],
  (results, member, teamId, showTrash) =>
    results.filter((wp) => {
      if (showTrash) {
        return (
          wp.owner_id && wp.owner_id === member.id && wp.team_id === teamId
        );
      }
      return (
        wp.owner_id &&
        wp.owner_id === member.id &&
        wp.team_id === teamId &&
        !wp.deleted_at
      );
    })
);

export const filterResultsByTeam = createSelector(
  [getAllWorkPackages, getTeamIdFromProps, getShowTrash],
  (results, teamId, showTrash) =>
    results.filter((wp) => {
      if (showTrash) {
        return wp.owner_id && wp.team_id === teamId;
      }
      return wp.owner_id && wp.team_id === teamId && !wp.deleted_at;
    })
);

export const filterOwnerlessResultsByTeam = createSelector(
  [getAllWorkPackages, getTeamFromProps],
  (results, team) =>
    results.filter((wp) => wp.team_id === team.id && !wp.owner_id)
);

export const filterChildrenResultsFromScope = createSelector(
  [getResultsFromProps, getResultFromProps, getShowTrash],
  (results, result, showTrash) =>
    results.filter((r) => {
      if (showTrash) {
        return r.parent_id === result.id;
      }
      return r.parent_id === result.id && !r.deleted_at;
    })
);

export const filterOutAcceptedStories = createSelector(
  [getPlacement, getStories],
  (placement, stories) => {
    if (placement === 'CadencePlanTab') {
      const { ACCEPTED, SHIPPABLE, LIVE } = WorkPackageStatuses;
      return stories.filter(
        (story) => ![ACCEPTED, SHIPPABLE, LIVE].includes(story.state)
      );
    }
  }
);
